import React, { useState } from 'react';
import { Field, Input, Button } from '../ui';
import { useCurrency } from '../../context/CurrencyContext';

interface ItemData {
  id: string;
  description: string;
  quantity: number;
  weight: number;
  price: number;
  madeIn: string;
  hsCode?: string;
}

interface EnhancedItemsListProps {
  items: ItemData[];
  parcelIndex: number;
  parcels: any[];
  setParcels: React.Dispatch<React.SetStateAction<any[]>>;
  countries: string[];
  theme: any; // kept for backward-compat; unused in new implementation
  initialEditingId?: string | null;
}

const validateItem = (item: ItemData): boolean => {
  return (
    item.description.length >= 10 &&
    item.quantity > 0 &&
    item.weight > 0 &&
    item.price > 0 &&
    Boolean(item.madeIn && item.madeIn.trim())
  );
};

const StatCard: React.FC<{ label: string; value: string; accent: string }> = ({
  label,
  value,
  accent,
}) => (
  <div
    style={{
      padding: 12,
      textAlign: 'center',
      borderRadius: 8,
      border: '1px solid var(--line)',
      background: `${accent}0d`,
    }}
  >
    <div style={{ fontSize: 18, fontWeight: 700, color: accent }}>{value}</div>
    <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{label}</div>
  </div>
);

const EnhancedItemsList: React.FC<EnhancedItemsListProps> = ({
  items,
  parcelIndex,
  parcels,
  setParcels,
  countries,
  initialEditingId = null,
}) => {
  const { currency } = useCurrency();
  const currencySymbols: Record<string, string> = { USD: '$', ILS: '₪', EUR: '€' };
  const symbol = currencySymbols[currency] ?? currency;

  const [editingItemId, setEditingItemId] = useState<string | null>(initialEditingId);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  React.useEffect(() => {
    if (initialEditingId) setEditingItemId(initialEditingId);
  }, [initialEditingId]);

  const filteredItems = items.filter(
    (item) =>
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.madeIn.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const updateParcels = (updatedItems: ItemData[]) => {
    const updated = [...parcels];
    updated[parcelIndex] = { ...parcels[parcelIndex], items: updatedItems };
    setParcels(updated);
  };

  const handleItemUpdate = (itemId: string, field: string, value: any) => {
    const updatedItems = [...(parcels[parcelIndex].items || [])];
    const idx = updatedItems.findIndex((i: ItemData) => i.id === itemId);
    if (idx !== -1) {
      (updatedItems[idx] as any)[field] = value;
      updateParcels(updatedItems);
    }
  };

  const handleItemRemove = (itemId: string) => {
    const updatedItems = (parcels[parcelIndex].items || []).filter(
      (i: ItemData) => i.id !== itemId
    );
    updateParcels(updatedItems);
    setSelectedItems((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  };

  const handleBulkRemove = () => {
    const updatedItems = (parcels[parcelIndex].items || []).filter(
      (i: ItemData) => !selectedItems.has(i.id)
    );
    updateParcels(updatedItems);
    setSelectedItems(new Set());
  };

  const handleSelectAll = () => {
    if (selectedItems.size === paginatedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(paginatedItems.map((item) => item.id)));
    }
  };

  const handleNumberChange = (itemId: string, field: string, val: string) => {
    if (val === '') { handleItemUpdate(itemId, field, ''); return; }
    const n = parseFloat(val);
    if (!isNaN(n) && n >= 0) handleItemUpdate(itemId, field, n);
  };

  const handleBlur = (itemId: string, field: string, val: any) => {
    if (val === '' || val === null || val === undefined) {
      const defaults: Record<string, number> = { quantity: 1, weight: 0.1, price: 0.01 };
      handleItemUpdate(itemId, field, defaults[field] ?? 0);
    }
  };

  const validCount = items.filter(validateItem).length;
  const totalValue = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const totalWeight = items.reduce((s, i) => s + i.weight * i.quantity, 0);

  return (
    <div>
      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        <StatCard label="Total Items" value={String(items.length)} accent="var(--brand-blue)" />
        <StatCard label="Valid Items" value={String(validCount)} accent="var(--ok)" />
        <StatCard label="Total Weight" value={`${totalWeight.toFixed(2)} kg`} accent="var(--info)" />
        <StatCard label="Total Value" value={`${symbol}${totalValue.toFixed(2)}`} accent="var(--brand-ink)" />
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          padding: '10px 14px',
          background: 'var(--bg-tint)',
          borderRadius: 6,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <input
          type="text"
          placeholder="Search items..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
          style={{
            padding: '6px 10px',
            border: '1px solid var(--line)',
            borderRadius: 6,
            fontSize: 13,
            background: 'var(--bg-elev)',
            color: 'var(--ink)',
            outline: 'none',
            minWidth: 180,
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {selectedItems.size > 0 && (
            <>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 10,
                  background: 'rgba(43,85,255,0.1)',
                  color: 'var(--brand-blue)',
                }}
              >
                {selectedItems.size} selected
              </span>
              <Button
                variant="secondary"
                onClick={handleBulkRemove}
                style={{ fontSize: 12, padding: '4px 10px', color: 'var(--err)' }}
              >
                🗑 Remove Selected
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={handleSelectAll} style={{ fontSize: 12, padding: '4px 10px' }}>
            {selectedItems.size === paginatedItems.length ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
      </div>

      {/* Items list */}
      {paginatedItems.map((item, itemIndex) => {
        const isEditing = editingItemId === item.id;
        const isValid = validateItem(item);
        const isSelected = selectedItems.has(item.id);
        const globalIndex = currentPage * itemsPerPage + itemIndex + 1;

        return (
          <div
            key={item.id}
            style={{
              marginBottom: 12,
              border: `2px solid ${isValid ? 'var(--ok)' : 'var(--warn, #f59e0b)'}`,
              borderRadius: 8,
              background: isSelected
                ? 'rgba(43,85,255,0.06)'
                : isValid
                ? 'rgba(15,163,114,0.02)'
                : 'rgba(245,158,11,0.02)',
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ padding: 14 }}>
              {!isEditing ? (
                /* Compact view */
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const next = new Set(selectedItems);
                          e.target.checked ? next.add(item.id) : next.delete(item.id);
                          setSelectedItems(next);
                        }}
                        style={{ width: 14, height: 14 }}
                      />
                      <span style={{ fontWeight: 600, fontSize: 13 }}>Item {globalIndex}</span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 7px',
                          borderRadius: 8,
                          background: isValid ? 'var(--ok-bg)' : 'rgba(245,158,11,0.1)',
                          color: isValid ? 'var(--ok)' : '#b45309',
                          border: `1px solid ${isValid ? 'var(--ok)' : '#f59e0b'}`,
                        }}
                      >
                        {isValid ? '✓ Valid' : '⚠ Needs Attention'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => setEditingItemId(item.id)}
                        style={{
                          width: 30, height: 30, borderRadius: '50%', border: 'none',
                          background: 'rgba(43,85,255,0.1)', cursor: 'pointer', fontSize: 14,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="Edit item"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => handleItemRemove(item.id)}
                        style={{
                          width: 30, height: 30, borderRadius: '50%', border: 'none',
                          background: 'rgba(220,38,38,0.1)', cursor: 'pointer', fontSize: 14,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="Remove item"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>Description</div>
                      <div
                        style={{
                          fontSize: 13,
                          color: item.description.length < 10 ? 'var(--err)' : 'var(--ink)',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        } as React.CSSProperties}
                      >
                        {item.description || 'No description'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>Origin</div>
                      <div style={{ fontSize: 13, color: !item.madeIn ? 'var(--err)' : 'var(--ink)' }}>
                        {item.madeIn || 'Not set'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>Qty</div>
                      <div style={{ fontSize: 13 }}>{item.quantity} pcs</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>Weight</div>
                      <div style={{ fontSize: 13 }}>{(item.weight * item.quantity).toFixed(2)} kg</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>Value</div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: item.price <= 0 ? 'var(--err)' : 'var(--brand-blue)',
                        }}
                      >
                        {symbol}{(item.price * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Edit mode */
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                      padding: '10px 14px',
                      background: 'rgba(43,85,255,0.05)',
                      borderRadius: 6,
                      border: '2px solid var(--brand-blue)',
                    }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-blue)' }}>
                      ✏️ Editing Item {globalIndex}
                    </span>
                    <Button
                      variant="blue"
                      onClick={() => setEditingItemId(null)}
                      style={{ fontSize: 12, padding: '5px 12px' }}
                    >
                      ✓ Done Editing
                    </Button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                    {/* Description */}
                    <Field label="Item Description *">
                      <textarea
                        required
                        placeholder="Describe the item (min 10 characters)"
                        value={item.description || ''}
                        onChange={(e) => handleItemUpdate(item.id, 'description', e.target.value)}
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: `1px solid ${item.description.length < 10 ? 'var(--err)' : 'var(--line)'}`,
                          borderRadius: 6,
                          fontSize: 13,
                          background: 'var(--bg-elev)',
                          color: 'var(--ink)',
                          resize: 'vertical',
                          outline: 'none',
                          boxSizing: 'border-box',
                          fontFamily: 'inherit',
                        }}
                      />
                      <p style={{ fontSize: 11, color: item.description.length < 10 ? 'var(--err)' : 'var(--ink-4)', margin: '3px 0 0' }}>
                        {item.description.length < 10
                          ? `Required: ${item.description.length}/10 characters minimum`
                          : `${item.description.length} characters`}
                      </p>
                    </Field>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
                      {/* Quantity */}
                      <Field label="Quantity *">
                        <Input
                          type="number"
                          required
                          value={item.quantity === 0 ? '' : item.quantity}
                          onChange={(e) => handleNumberChange(item.id, 'quantity', e.target.value)}
                          onBlur={() => handleBlur(item.id, 'quantity', item.quantity)}
                          min={1}
                          style={{ borderColor: item.quantity <= 0 ? 'var(--err)' : undefined }}
                        />
                        {item.quantity <= 0 && (
                          <p style={{ fontSize: 11, color: 'var(--err)', margin: '3px 0 0' }}>
                            Must be at least 1
                          </p>
                        )}
                      </Field>

                      {/* Weight */}
                      <Field label="Weight/item (kg) *">
                        <Input
                          type="number"
                          required
                          value={item.weight === 0 ? '' : item.weight}
                          onChange={(e) => handleNumberChange(item.id, 'weight', e.target.value)}
                          onBlur={() => handleBlur(item.id, 'weight', item.weight)}
                          min={0.1}
                          step={0.1}
                          style={{ borderColor: item.weight <= 0 ? 'var(--err)' : undefined }}
                        />
                        <p style={{ fontSize: 11, color: item.weight <= 0 ? 'var(--err)' : 'var(--ink-4)', margin: '3px 0 0' }}>
                          {item.weight <= 0 ? 'Must be > 0' : `Total: ${(item.weight * item.quantity).toFixed(2)} kg`}
                        </p>
                      </Field>

                      {/* Price */}
                      <Field label={`Price/item (${currency}) *`}>
                        <Input
                          type="number"
                          required
                          value={item.price === 0 ? '' : item.price}
                          onChange={(e) => handleNumberChange(item.id, 'price', e.target.value)}
                          onBlur={() => handleBlur(item.id, 'price', item.price)}
                          min={0.01}
                          step={0.01}
                          style={{ borderColor: item.price <= 0 ? 'var(--err)' : undefined }}
                        />
                        <p style={{ fontSize: 11, color: item.price <= 0 ? 'var(--err)' : 'var(--ink-4)', margin: '3px 0 0' }}>
                          {item.price <= 0
                            ? `Must be > ${symbol}0.00`
                            : `Total: ${symbol}${(item.price * item.quantity).toFixed(2)}`}
                        </p>
                      </Field>

                      {/* Made In */}
                      <Field label="Made in *">
                        <select
                          value={item.madeIn || ''}
                          onChange={(e) => handleItemUpdate(item.id, 'madeIn', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '7px 10px',
                            border: `1px solid ${!item.madeIn ? 'var(--err)' : 'var(--line)'}`,
                            borderRadius: 6,
                            fontSize: 13,
                            background: 'var(--bg-elev)',
                            color: 'var(--ink)',
                          }}
                        >
                          <option value="">Select country</option>
                          {countries.map((country) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </select>
                        {!item.madeIn && (
                          <p style={{ fontSize: 11, color: 'var(--err)', margin: '3px 0 0' }}>
                            Required
                          </p>
                        )}
                      </Field>
                    </div>

                    {/* HS Code (optional, shown on the customs invoice) */}
                    <Field label="HS Code">
                      <Input
                        value={item.hsCode || ''}
                        onChange={(e) => handleItemUpdate(item.id, 'hsCode', e.target.value)}
                        placeholder="e.g. 6109.10"
                      />
                    </Field>

                    {/* Item totals summary */}
                    <div
                      style={{
                        padding: '10px 14px',
                        background: 'rgba(43,85,255,0.04)',
                        border: '1px solid rgba(43,85,255,0.15)',
                        borderRadius: 6,
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: 12,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>Total Quantity</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{item.quantity} pcs</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>Total Weight</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {(item.weight * item.quantity).toFixed(2)} kg
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>Total Value</div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: item.price <= 0 ? 'var(--err)' : 'var(--brand-blue)',
                          }}
                        >
                          {symbol}{(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 20,
            gap: 16,
          }}
        >
          <Button
            variant="secondary"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage(currentPage - 1)}
            style={{ fontSize: 13, padding: '5px 12px' }}
          >
            ← Previous
          </Button>
          <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>
            Page {currentPage + 1} of {totalPages} • {filteredItems.length} items
          </span>
          <Button
            variant="secondary"
            disabled={currentPage === totalPages - 1}
            onClick={() => setCurrentPage(currentPage + 1)}
            style={{ fontSize: 13, padding: '5px 12px' }}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
};

export default EnhancedItemsList;
